import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../apiClient';

const TONS = [
  { value: 'informatif', label: 'Informatif' },
  { value: 'humoristique', label: 'Humoristique' },
  { value: 'detaille', label: 'Détaillé' },
  { value: 'percutant', label: 'Percutant' },
  { value: 'fan', label: 'Fan' },
];

function stripHtml(html) {
  return String(html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function Veille() {
  const [sources, setSources] = useState([]);
  const [selectedSourceId, setSelectedSourceId] = useState(null);
  const [sourceArticles, setSourceArticles] = useState([]);
  const [generatedArticles, setGeneratedArticles] = useState([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [reformulatingUrl, setReformulatingUrl] = useState('');
  const [articleTons, setArticleTons] = useState({});
  const [previewArticle, setPreviewArticle] = useState(null);
  const [schedulingArticle, setSchedulingArticle] = useState(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [publishingId, setPublishingId] = useState('');
  const [socialDrafts, setSocialDrafts] = useState({});
  const [error, setError] = useState('');

  const pendingArticles = useMemo(
    () => generatedArticles.filter((article) => article.statut === 'en_attente'),
    [generatedArticles]
  );

  const historyArticles = useMemo(
    () => generatedArticles.filter((article) => article.statut !== 'en_attente'),
    [generatedArticles]
  );

  useEffect(() => {
    setSocialDrafts((current) => {
      const next = { ...current };
      generatedArticles.forEach((article) => {
        if (article.statut !== 'en_attente') return;
        if (!next[article.id]) {
          next[article.id] = {
            postFacebook: article.postFacebook || '',
            postInstagram: article.postInstagram || '',
          };
        }
      });
      return next;
    });
  }, [generatedArticles]);

  const loadSources = async () => {
    const response = await apiFetch('/api/veille/sources');
    if (!response.ok) throw new Error('Erreur chargement sources');
    const data = await response.json();
    setSources(Array.isArray(data) ? data : []);
  };

  const loadGeneratedArticles = async () => {
    const response = await apiFetch('/api/veille/articles-generes');
    if (!response.ok) throw new Error('Erreur chargement articles générés');
    const data = await response.json();
    setGeneratedArticles(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    const bootstrap = async () => {
      try {
        setError('');
        setLoadingSources(true);
        await Promise.all([loadSources(), loadGeneratedArticles()]);
      } catch {
        setError('Impossible de charger le module Veille.');
      } finally {
        setLoadingSources(false);
      }
    };
    bootstrap();
  }, []);

  const fetchSourceArticles = async (sourceId) => {
    try {
      setError('');
      setSelectedSourceId(sourceId);
      setLoadingArticles(true);
      const response = await apiFetch(`/api/veille/articles/${sourceId}`);
      if (!response.ok) throw new Error('Erreur chargement articles source');
      const data = await response.json();
      setSourceArticles(Array.isArray(data) ? data : []);
    } catch {
      setError('Impossible de récupérer les articles de cette source.');
      setSourceArticles([]);
    } finally {
      setLoadingArticles(false);
    }
  };

  const handleReformulate = async (article, sourceId) => {
    const ton = articleTons[article.url] || 'informatif';
    try {
      setError('');
      setReformulatingUrl(article.url);
      const response = await apiFetch('/api/veille/reformuler', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          articleUrl: article.url,
          ton,
          sourceId,
          siteId: null,
        }),
      });
      if (!response.ok) throw new Error('Erreur reformulation');
      setSourceArticles((current) => current.filter((item) => item.url !== article.url));
      await loadGeneratedArticles();
    } catch {
      setError('Impossible de reformuler cet article.');
    } finally {
      setReformulatingUrl('');
    }
  };

  const updateSocialDraft = (articleId, field, value) => {
    setSocialDrafts((current) => ({
      ...current,
      [articleId]: {
        ...current[articleId],
        [field]: value,
      },
    }));
  };

  const saveSocialDraft = async (articleId) => {
    const draft = socialDrafts[articleId];
    if (!draft) return;
    const response = await apiFetch(`/api/veille/articles-generes/${articleId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        postFacebook: draft.postFacebook,
        postInstagram: draft.postInstagram,
      }),
    });
    if (!response.ok) throw new Error('Erreur sauvegarde posts sociaux');
  };

  const updateArticleStatus = async (articleId, statut) => {
    const response = await apiFetch(`/api/veille/articles-generes/${articleId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ statut }),
    });
    if (!response.ok) throw new Error('Erreur mise à jour statut');
    await loadGeneratedArticles();
  };

  const handleValidate = (article) => {
    setSchedulingArticle({
      ...article,
      postFacebook: socialDrafts[article.id]?.postFacebook ?? article.postFacebook ?? '',
      postInstagram: socialDrafts[article.id]?.postInstagram ?? article.postInstagram ?? '',
    });
    const defaultDate = new Date(Date.now() + 60 * 60 * 1000);
    const local = new Date(defaultDate.getTime() - defaultDate.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
    setScheduleDate(local);
  };

  const handleSchedulePublish = async () => {
    if (!schedulingArticle?.id || !scheduleDate) return;
    try {
      setError('');
      setPublishingId(schedulingArticle.id);
      const isoDate = new Date(scheduleDate).toISOString();
      const draft = socialDrafts[schedulingArticle.id] || {
        postFacebook: schedulingArticle.postFacebook || '',
        postInstagram: schedulingArticle.postInstagram || '',
      };

      const updateResponse = await apiFetch(`/api/veille/articles-generes/${schedulingArticle.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          statut: 'valide',
          datePublication: isoDate,
          postFacebook: draft.postFacebook,
          postInstagram: draft.postInstagram,
        }),
      });
      if (!updateResponse.ok) throw new Error('Erreur programmation');

      const publishResponse = await apiFetch(`/api/veille/publier/${schedulingArticle.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!publishResponse.ok) throw new Error('Erreur publication');

      setSchedulingArticle(null);
      setScheduleDate('');
      await loadGeneratedArticles();
    } catch {
      setError('Impossible de programmer la publication.');
    } finally {
      setPublishingId('');
    }
  };

  const getStatutClass = (statut) => {
    if (statut === 'publie') return 'published';
    if (statut === 'valide') return 'published';
    if (statut === 'refuse') return 'generated';
    return 'generated';
  };

  return (
    <section className="contenus-page">
      <header className="contenus-header">
        <h1>Veille — MAD Breizh</h1>
        <p>Récupération, reformulation et publication d&apos;actualités metal pour mad-breizh.com</p>
      </header>

      {error && <p className="sites-error">{error}</p>}

      <section className="contenus-section">
        <h2>Sources &amp; Récupération</h2>
        {loadingSources ? (
          <div className="sites-empty-card">
            <p>Chargement des sources...</p>
          </div>
        ) : (
          <>
            <div className="article-actions" style={{ flexWrap: 'wrap' }}>
              {sources.map((source) => (
                <button
                  key={source.id}
                  type="button"
                  className={selectedSourceId === source.id ? 'primary-button' : 'secondary-button'}
                  onClick={() => fetchSourceArticles(source.id)}
                >
                  {source.nom}
                </button>
              ))}
            </div>

            {loadingArticles ? (
              <div className="sites-empty-card">
                <p>Récupération des articles...</p>
              </div>
            ) : sourceArticles.length === 0 ? (
              <div className="sites-empty-card">
                <p>Sélectionnez une source pour afficher les 10 derniers articles.</p>
              </div>
            ) : (
              <div className="contenus-articles-grid">
                {sourceArticles.map((article) => (
                  <article className="article-card" key={article.url}>
                    <div className="article-card-header">
                      <h3>{article.titre}</h3>
                    </div>
                    <p className="article-excerpt">
                      {article.date ? `${article.date} — ` : ''}
                      {article.resume || 'Sans résumé'}
                    </p>
                    <p className="article-excerpt">
                      <a href={article.url} target="_blank" rel="noreferrer">
                        Voir la source
                      </a>
                    </p>
                    <div className="article-actions">
                      <select
                        value={articleTons[article.url] || 'informatif'}
                        onChange={(event) =>
                          setArticleTons((current) => ({
                            ...current,
                            [article.url]: event.target.value,
                          }))
                        }
                      >
                        {TONS.map((ton) => (
                          <option key={ton.value} value={ton.value}>
                            {ton.label}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="sujet-validate-button"
                        disabled={reformulatingUrl === article.url}
                        onClick={() => handleReformulate(article, selectedSourceId)}
                      >
                        {reformulatingUrl === article.url ? 'Reformulation...' : 'Reformuler'}
                      </button>
                      <button
                        type="button"
                        className="site-delete-button"
                        disabled={reformulatingUrl === article.url}
                        onClick={() =>
                          setSourceArticles((current) =>
                            current.filter((item) => item.url !== article.url)
                          )
                        }
                      >
                        Refuser
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </>
        )}
      </section>

      <section className="contenus-section">
        <h2>Articles en attente de validation</h2>
        {pendingArticles.length === 0 ? (
          <div className="sites-empty-card">
            <p>Aucun article en attente de validation.</p>
          </div>
        ) : (
          <div className="contenus-articles-grid">
            {pendingArticles.map((article) => (
              <article className="article-card" key={article.id}>
                <div className="article-card-header">
                  <h3>{article.titre}</h3>
                  <span className="article-status generated">en_attente</span>
                </div>
                <p className="article-excerpt">
                  <strong>Ton :</strong> {article.ton}
                </p>
                <p className="article-excerpt">{stripHtml(article.contenuHtml).slice(0, 200)}...</p>

                <div className="publish-form" style={{ marginTop: '0.75rem' }}>
                  <label htmlFor={`fb-${article.id}`}>Post Facebook</label>
                  <textarea
                    id={`fb-${article.id}`}
                    rows={4}
                    value={socialDrafts[article.id]?.postFacebook ?? article.postFacebook ?? ''}
                    onChange={(event) =>
                      updateSocialDraft(article.id, 'postFacebook', event.target.value)
                    }
                    onBlur={() => saveSocialDraft(article.id).catch(() => {})}
                  />

                  <label htmlFor={`ig-${article.id}`}>Post Instagram</label>
                  <textarea
                    id={`ig-${article.id}`}
                    rows={3}
                    value={socialDrafts[article.id]?.postInstagram ?? article.postInstagram ?? ''}
                    onChange={(event) =>
                      updateSocialDraft(article.id, 'postInstagram', event.target.value)
                    }
                    onBlur={() => saveSocialDraft(article.id).catch(() => {})}
                  />
                </div>

                <div className="article-actions">
                  <button
                    type="button"
                    className="sujet-validate-button"
                    onClick={() => handleValidate(article)}
                  >
                    Valider
                  </button>
                  <button
                    type="button"
                    className="site-delete-button"
                    onClick={() => updateArticleStatus(article.id, 'refuse')}
                  >
                    Refuser
                  </button>
                  <button
                    type="button"
                    className="site-edit-button"
                    onClick={() => setPreviewArticle(article)}
                  >
                    Prévisualiser
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="contenus-section">
        <h2>Articles publiés / refusés</h2>
        {historyArticles.length === 0 ? (
          <div className="sites-empty-card">
            <p>Aucun historique pour le moment.</p>
          </div>
        ) : (
          <div className="contenus-articles-grid">
            {historyArticles.map((article) => (
              <article className="article-card" key={article.id}>
                <div className="article-card-header">
                  <h3>{article.titre}</h3>
                  <span className={`article-status ${getStatutClass(article.statut)}`}>
                    {article.statut}
                  </span>
                </div>
                <p className="article-excerpt">
                  <strong>Ton :</strong> {article.ton}
                </p>
                {article.datePublication && (
                  <p className="article-excerpt">
                    <strong>Publication :</strong>{' '}
                    {new Date(article.datePublication).toLocaleString('fr-FR')}
                  </p>
                )}
                <div className="article-actions">
                  <button
                    type="button"
                    className="site-edit-button"
                    onClick={() => setPreviewArticle(article)}
                  >
                    Prévisualiser
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {previewArticle && (
        <div className="sites-modal-overlay" role="presentation" onClick={() => setPreviewArticle(null)}>
          <div
            className="sites-modal contenus-article-modal"
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="sites-modal-header">
              <h2>{previewArticle.titre}</h2>
              <button type="button" className="sites-close-button" onClick={() => setPreviewArticle(null)}>
                ×
              </button>
            </header>
            <div
              className="article-html-content"
              dangerouslySetInnerHTML={{ __html: previewArticle.contenuHtml || '' }}
            />
          </div>
        </div>
      )}

      {schedulingArticle && (
        <div className="sites-modal-overlay" role="presentation" onClick={() => setSchedulingArticle(null)}>
          <div
            className="sites-modal contenus-publish-modal"
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="sites-modal-header">
              <h2>Programmer la publication</h2>
              <button type="button" className="sites-close-button" onClick={() => setSchedulingArticle(null)}>
                ×
              </button>
            </header>
            <div className="publish-form">
              <p className="publish-target-title">{schedulingArticle.titre}</p>
              <label htmlFor="veilleScheduleDate">Date et heure de publication</label>
              <input
                id="veilleScheduleDate"
                type="datetime-local"
                value={scheduleDate}
                onChange={(event) => setScheduleDate(event.target.value)}
              />
              <div className="sites-form-actions">
                <button type="button" className="secondary-button" onClick={() => setSchedulingArticle(null)}>
                  Annuler
                </button>
                <button
                  type="button"
                  className="primary-button"
                  disabled={!scheduleDate || publishingId === schedulingArticle.id}
                  onClick={handleSchedulePublish}
                >
                  {publishingId === schedulingArticle.id ? 'Publication...' : 'Programmer la publication'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default Veille;
